'use client';

import { cn } from '@coas/ui/lib';
import { ScrollArea, ScrollBar } from '@coas/ui/scroll-area';
import * as React from 'react';
import type { CategoryTreeNode, ClearCategory, IndicatorsStatus, TriAction } from '../model/types';
import { CategoryTreeRow } from './CategoryTreeRow';

type CategoryTreeProps = {
    items: readonly ClearCategory[];
    statusById: ReadonlyMap<string, IndicatorsStatus>;
    actions: ReadonlyMap<string, TriAction>;
    onAction: (id: string, next: TriAction) => void;
    disabled?: boolean;
    initialExpandedIds?: readonly string[];
    className?: string;
};

export const CategoryTree: React.FC<CategoryTreeProps> = ({
    items,
    statusById,
    actions,
    onAction,
    disabled,
    initialExpandedIds,
    className,
}) => {
    const nodes = React.useMemo(() => {
        const byId = new Map<string, CategoryTreeNode>();
        const childrenOf = new Map<string | null, string[]>();

        for (const c of items) {
            const id = c.id;
            const parentId = c.parentId ?? null;
            const label = c.name;

            const list = childrenOf.get(parentId);
            if (list === undefined) childrenOf.set(parentId, [id]);
            else list.push(id);

            byId.set(id, { id, label, parentId, children: [] });
        }

        for (const [pid, list] of childrenOf.entries()) {
            if (pid !== null) {
                const p = byId.get(pid);
                if (p !== undefined) p.children = list;
            }
        }

        const roots: string[] = [];
        const fromNull = childrenOf.get(null);
        if (Array.isArray(fromNull)) for (const id of fromNull) roots.push(id);
        for (const n of byId.values()) {
            if (n.parentId !== null && byId.get(n.parentId) === undefined) roots.push(n.id);
        }

        return { byId, roots };
    }, [items]);

    const [expanded, setExpanded] = React.useState<Set<string>>(() => {
        const s = new Set<string>();
        if (Array.isArray(initialExpandedIds)) for (const id of initialExpandedIds) s.add(id);
        return s;
    });

    const handleToggle = React.useCallback((id: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const rows = React.useMemo(() => {
        const acc: React.ReactElement[] = [];

        const walk = (id: string, depth: number) => {
            const n = nodes.byId.get(id);
            if (n === undefined) return;

            const hasChildren = n.children.length > 0;
            const isExpanded = expanded.has(id);
            const status = statusById.get(id) ?? 'none';
            const action = actions.get(id) ?? 'none';

            acc.push(
                <CategoryTreeRow
                    key={n.id}
                    id={n.id}
                    label={n.label}
                    depth={depth}
                    hasChildren={hasChildren}
                    isExpanded={isExpanded}
                    status={status}
                    action={action}
                    disabled={disabled}
                    onToggle={hasChildren ? handleToggle : undefined}
                    onAction={onAction}
                />,
            );

            if (hasChildren && isExpanded) {
                for (const childId of n.children) walk(childId, depth + 1);
            }
        };

        for (const rootId of nodes.roots) walk(rootId, 0);
        return acc;
    }, [actions, disabled, expanded, nodes.byId, nodes.roots, onAction, statusById, handleToggle]);

    return (
        <div
            className={cn(
                'relative w-full overflow-hidden',
                'h-[clamp(150px,40dvh,450px)]',
                'rounded-10 border border-neutral-200 bg-white',
                className,
            )}
        >
            <div className='absolute inset-0 overflow-hidden'>
                <ScrollArea className='h-full w-full overflow-auto'>
                    <div className='min-w-max whitespace-nowrap p-1 pe-2 pb-2'>
                        <div className='flex flex-col gap-0.5'>{rows}</div>
                    </div>

                    <ScrollBar orientation='vertical' />
                    <ScrollBar orientation='horizontal' />
                </ScrollArea>
            </div>
        </div>
    );
};
